import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../../../DemoState/const";
import loader from "../../../../../../../components/recharts/loader.gif";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../../../../utility/displayText";

interface SummaryData {
  date: string;
  month: string;
  oar: number;
  gna_cost: number;
  gna_quantum: number;
  tgna_cost: number;
  tgna_quantum: number;
  optimal_gna: number;
  total_cost: number;
  slot: number;
}

interface ScenarioCapacityData {
  scenario_id: number;
  scenario_number: number;
  is_active: boolean;
  is_finalized: boolean;
  dates: Array<{
    year: number;
    date: string;
    sources: Array<{
      source: string;
      total_capacity: string;
    }>;
  }>;
}

export default function MonthVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock?: any[];
  refreshKey?: number;
  setError?: any;
  loading: boolean;
  data: any[];
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  // --- OLD CHART CONFIG ---
  const chartSuffixes = [
    {
      suffix: "tgna_quantum",
      displayName: "Optimal TGNA Utilisation",
      stackSuffix: "tgna_quantum",
      stroke: "rgba(255, 194, 158, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "gna_quantum",
      displayName: "Optimal GNA Utilisation",
      stackSuffix: "gna_quantum",
      stroke: "rgba(241, 147, 92, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "optimal_gna",
      displayName: "Optimal GNA",
      stackSuffix: "optimal_gna",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      isDash: true,
    },
    {
      suffix: "oar",
      displayName: "Total Open Access Requirement",
      stackSuffix: "oar",
      stroke: "rgba(17, 141, 255, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
  ];

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  const sourceColors: any = {
    solar: "#FF9F43", // Pastel Orange
    wind: "#54A0FF", // Pastel Blue
    hydro: "rgba(80, 152, 89, 1)", // Pastel Green
    thermal: "#C6F0ED", // Pastel Red
    biomass: "#5F27CD", // Purple
    bagasse: "#FF9FF3", // Pinkish
    gas: "#FECA57", // Yellowish
    nuclear: "#8395A7", // Grey
    hybrid: "#00D2D3", // Teal
    "small hydro": "#10AC84", // Darker Green
    bess: "#2E86DE", // Strong Blue
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // CHECK DATA TYPE
    const isScenarioData =
      data[0] && "dates" in data[0] && Array.isArray(data[0].dates);

    if (isScenarioData) {
      const transformedData: any[] = [];
      const allYears = new Set<number>();

      data.forEach((scenario: any) => {
        scenario.dates?.forEach((dateData: any) => allYears.add(dateData.year));
      });

      Array.from(allYears)
        .sort()
        .forEach((year) => {
          const row: any = { year: year.toString() };

          data.forEach((scenario: any) => {
            const scenarioLabel = `Scenario_${scenario.scenario_number}`;
            const yearData = scenario.dates?.find((d: any) => d.year === year);

            if (yearData) {
              yearData.sources?.forEach((source: any) => {
                const key = `${scenarioLabel}_${source.source}`;
                row[key] = parseFloat(source.total_capacity || "0");
              });
            }
          });

          transformedData.push(row);
        });

      const keys: string[] = [];
      if (transformedData.length > 0) {
        Object.keys(transformedData[0]).forEach((key) => {
          if (key !== "year") keys.push(key);
        });
      }

      setChartData(transformedData);
      setChartKeys(keys);
    } else {
      // --- OLD LOGIC ---
      let filteredData = data;
      // if (selectedBlock && selectedBlock.length > 0) {
      //   const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      //   filteredData = filteredData.filter((row: any) =>
      //     blocks.includes(Number(row.time_slot))
      //   );
      // }

      const par: ChartDataParams = {
        keys: ["date", "slot", "month"],
        tableKeys: ["date", "slot", "month"],
        groupBy: [""],
        volumeKey: ``,
        aggregationKeys: ["gna_quantum", "tgna_quantum", "optimal_gna", "oar"],
        averageKeys: [],
        averageDuplicationKeys: ["date", "slot", "month"],
        priceKey: "",
        data: filteredData,
      };

      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
    }
  }, [data, selectedBlock]);

  const createChartObject = (
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    fill: string,
    yAxisId: string,
    isDash?: boolean,
  ) => {
    return {
      dataKey: suffix,
      name: displayName,
      legendColor: fill === "none" ? stroke : fill,
      type: chartType,
      unit: unit,
      stroke: stroke,
      fill: fill,
      yAxisId: yAxisId,
      stackId: stackSuffix || undefined,
      isDash: isDash,
    };
  };

  const createOldChartObject = (
    key: string,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string,
    isDash: boolean,
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: legendColor,
      type: ["optimal_gna", "oar"].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: undefined,
      isDash: isDash,
    };
  };

  function getLegends() {
    if (chartData.length === 0) return [];

    if (chartKeys.length > 0 && chartKeys[0].includes("Scenario")) {
      return chartKeys.map((key: string) => {
        const parts = key.split("_");
        const scenarioNum = parts[1];
        const sourceName = parts.slice(2).join("_");
        const sourceLower = sourceName.toLowerCase();
        const stroke = sourceColors[sourceLower] || "#000000";

        const formattedSource = sourceLower
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        const displayName = `Scenario ${scenarioNum} ${formattedSource}`;
        const stackSuffix = `${parts[0]}_${scenarioNum}`;

        return createChartObject(
          key, // dataKey (suffix)
          displayName, // name
          stackSuffix, // stackId
          ChartType.Bar, // type
          "MW", // unit
          stroke, // stroke
          stroke, // fill
          "left", // yAxisId
        );
      });
    }

    // OLD LEGENDS
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
        isDash,
      }) =>
        chartKeys.map(
          (key, index) =>
            createOldChartObject(
              key,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId,
              isDash,
            ) as any,
        ),
    );
  }

  const isScenario = chartKeys.length > 0 && chartKeys[0].includes("Scenario");

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center h-[400px]">
          <p className="text-gray-500 !text-sm">
            Please Select Scenarios to View Data
          </p>
        </div>
      ) : (
        <ReMixChart
          tooltipBoundary="viewport"
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={
            !isScenario && chartData.length > 96 * 2 ? true : undefined
          }
          brushStart={BrushStart.Start}
          isTimeSlot={false}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="MW"
          nameGap={40}
          xDataKey={isScenario ? "year" : "month"}
          top={10}
          bottom={30}
          xTick={
            !isScenario
              ? (value: any, index: any) => {
                  return renderHourTick(value, 10);
                }
              : undefined
          }
          showToolbox={false}
        />
      )}
    </>
  );
}
